'use client'

import {
  PusherProvider as $PusherProvider,
  type PusherProviderProps,
} from '@harelpls/use-pusher'
import { getPublicEnv } from '@tucan/utils'
import { message } from 'antd'
import { ActivityListener } from './_components/activity.listener'
import { PushAuthenticationListener } from './_components/push.authentication.listener'
import { CompanyFileDownloader } from './_components/company.file.downloader'
import { CompanyTranslationLoader } from './_components/company.translation.loader'
import OpenGraph from 'components/open.graph'
import { StorageDisabledWarning } from './_components/storage.disabled.warning'
import { TriggerListener } from './_components/trigger.listener'
import { UploadIndicators } from './_components/upload.indicators'
import { useProfile } from 'hooks/use.profile'
import i18n from 'i18next'
import { usePathname, useSearchParams } from 'next/navigation'
import posthog from 'posthog-js'
import { FC, PropsWithChildren, useEffect } from 'react'
import { getBoolean } from 'utils/get.boolean'

const PusherProvider = $PusherProvider as FC<
  PropsWithChildren<PusherProviderProps>
>

/**
 * we want to call it all the time -> as this will also register the user in analytics
 */
const RegisterAnalytics: FC = () => {
  useProfile()

  return null
}

/**
 * Replaces the pages-router `router.events.on('routeChangeComplete')` PostHog pageview capture.
 */
const PageViewTracker: FC = () => {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    posthog?.capture('$pageview')
  }, [pathname, searchParams])

  return null
}

/**
 * Ports the imperative `_app` effects: antd message offset + keeping `<html lang>` in sync.
 */
const AppChrome: FC = () => {
  useEffect(() => {
    message.config({
      top: 70,
    })

    const listener = (lang: string) => {
      const html = document.querySelector('html')

      if (html) {
        html.setAttribute('lang', lang)
      }
    }

    listener(i18n.language)

    i18n.on('languageChanged', listener)

    return () => {
      i18n.off('languageChanged', listener)
    }
  }, [])

  return null
}

/**
 * Runtime providers + listeners that in the pages router lived inside `_app.tsx`.
 * Rendered inside the Apollo/Redux/Session/Antd provider tree so every route shares them.
 */
export function RuntimeProviders({ children }: PropsWithChildren) {
  return (
    <PusherProvider
      clientKey={getPublicEnv('PUSHER_CLIENT_KEY')}
      cluster={getPublicEnv('PUSHER_CLUSTER')}
      wsHost={getPublicEnv('PUSHER_HOST')}
      enabledTransports={
        getBoolean(getPublicEnv('PUSHER_ENABLED')) ? ['ws', 'wss'] : []
      }
      userAuthentication={{
        endpoint: '/api/auth/push/user',
        transport: 'ajax',
      }}
      channelAuthorization={{
        endpoint: '/api/auth/push/channel',
        transport: 'ajax',
      }}
    >
      <AppChrome />
      <PageViewTracker />
      <CompanyFileDownloader />
      <CompanyTranslationLoader />
      <PushAuthenticationListener />
      <ActivityListener />
      <RegisterAnalytics />
      <StorageDisabledWarning />
      <UploadIndicators />
      <OpenGraph />
      <TriggerListener />
      {children}
    </PusherProvider>
  )
}
